import React from 'react';
import { PortalName } from '@/common/DataModel/PortalData';
import {
  UmrFooterSection,
  UmrFooterWrapper,
  UmrFooterLink,
  UmrFooterLinkText,
  GehaFooterWrapper,
  GehaFooterLink,
} from './styles';
import { useLagoon } from '@/hooks/useLagoon';
import { FooterLinks } from './constants';
import { useGetThemePortalProps } from '@/hooks/useGetThemePortalProps';
import { formatLagoonData } from '../../../utils/formatLagoonData';
import { FooterLink, IAdobeEventClickedTagData } from './types';

// TODO: Update any use of locale to use actual selected locale
export const CommonFooter = () => {
  const { portalName, lob } = useGetThemePortalProps();

  const data = useLagoon('container-ui-messaging')();

  const footerData = formatLagoonData(data, FooterLinks);

  const trackAdobeEventOnClick = ({
    destinationUrl,
    location,
    name,
  }: IAdobeEventClickedTagData) => {
    const tagData = {
      destinationUrl,
      lob,
      location: 'footer navigation>' + location,
      name,
      type: 'external',
    };

    window.adobeDataLayer.push({
      event: 'link track',
      component: {
        name: 'global navigation',
      },
      link: tagData,
    });
  };

  const UmrFooter = () => {
    const subFooterLinks = [
      {
        label: footerData[FooterLinks.TERMS_OF_USE]?.title,
        href: footerData[FooterLinks.TERMS_OF_USE]?.url,
      },
      {
        label: footerData[FooterLinks.PRIVACY_POLICY]?.title,
        href: footerData[FooterLinks.PRIVACY_POLICY]?.url,
      },
      {
        label: footerData[FooterLinks.ABOUT_UNITED_HEALTH_CARE]?.title,
        href: footerData[FooterLinks.ABOUT_UNITED_HEALTH_CARE]?.url,
      },
    ];

    const importantInformationFooterSection: FooterLink[] = [
      {
        href: footerData[FooterLinks.NOTICE_DISCLOSURE]?.url,
        label: footerData[FooterLinks.NOTICE_DISCLOSURE]?.title,
        testId: 'notices-and-disclosures',
        location: 'important information',
        name: 'notices and disclosures',
      },
      {
        href: footerData[FooterLinks.PROVIDER_DATA_INFORMATION]?.url,
        label: footerData[FooterLinks.PROVIDER_DATA_INFORMATION]?.title,
        testId: 'provider-data-information',
        location: 'important information',
        name: 'provider data information',
      },
      {
        href: footerData[FooterLinks.LEGAL_ENTITIES]?.url,
        label: footerData[FooterLinks.LEGAL_ENTITIES]?.title,
        testId: 'legal-entities',
        location: 'important information',
        name: 'legal entities',
      },
      {
        href: footerData[
          FooterLinks
            .STATE_SPECIFIC_EMPLOYER_INDIVIDUAL_EXCHANGE_PLAN_INFORMATION
        ]?.url,
        label:
          footerData[
            FooterLinks
              .STATE_SPECIFIC_EMPLOYER_INDIVIDUAL_EXCHANGE_PLAN_INFORMATION
          ]?.title,
        testId: 'state-individual-plan-information',
        location: 'important information',
        name: 'State Specific Employer, Individual, Exchange Plan Information',
      },
    ];

    const accessibilityFooterSection: FooterLink[] = [
      {
        href: footerData[FooterLinks.ACCESSIBILITY_STATEMENT]?.url,
        label: footerData[FooterLinks.ACCESSIBILITY_STATEMENT]?.title,
        testId: 'accessibility-statement',
        location: 'accessibility',
        name: 'notices and disclosures',
      },
      {
        href: footerData[
          FooterLinks.LANGUAGE_ASSISTANCE_NON_DISCRIMINATION_NOTICE
        ]?.url,
        label:
          footerData[FooterLinks.LANGUAGE_ASSISTANCE_NON_DISCRIMINATION_NOTICE]
            ?.title,
        testId: 'language-and-non-discrimination-notice',
        location: 'accessibility',
        name: 'notices and disclosures',
      },
    ];

    return (
      <UmrFooterWrapper
        subFooterLinks={subFooterLinks}
        subFooterTitle="United HealthCare Services, Inc. All rights reserved."
        spreadSections={true}
      >
        <UmrFooterSection
          title={footerData[FooterLinks.IMPORTANT_INFORMATION]?.title}
          headingLevel={2}
        >
          {importantInformationFooterSection.map((link, index) => (
            <UmrFooterLink
              data-testid={`umr-footer-${link.testId}`}
              href={link.href}
              key={index}
              target="_blank"
              onClick={() =>
                trackAdobeEventOnClick({
                  destinationUrl: link.href,
                  location: link.location,
                  name: link.name,
                })
              }
            >
              <UmrFooterLinkText>{link.label}</UmrFooterLinkText>
            </UmrFooterLink>
          ))}
        </UmrFooterSection>
        <UmrFooterSection title={footerData[FooterLinks.ACCESSIBILITY].title}>
          {accessibilityFooterSection.map((link, index) => (
            <UmrFooterLink
              data-testid={`umr-footer-${link.testId}`}
              href={link.href}
              key={index}
              target="_blank"
              onClick={() =>
                trackAdobeEventOnClick({
                  destinationUrl: link.href,
                  location: link.location,
                  name: link.name,
                })
              }
            >
              <UmrFooterLinkText>{link.label}</UmrFooterLinkText>
            </UmrFooterLink>
          ))}
        </UmrFooterSection>
      </UmrFooterWrapper>
    );
  };

  const GehaFooter = () => {
    const links = [
      {
        href: footerData[FooterLinks.GEHA_CONTINUITY_OF_CARE]?.url,
        label: footerData[FooterLinks.GEHA_CONTINUITY_OF_CARE]?.title,
        testId: 'continuity-of-care',
      },
      {
        href: footerData[FooterLinks.GEHA_PRIVACY_POLICY]?.url,
        label: footerData[FooterLinks.GEHA_PRIVACY_POLICY]?.title,
        testId: 'privacy-policy',
      },
      {
        href: footerData[FooterLinks.GEHA_TERMS_OF_USE]?.url,
        label: footerData[FooterLinks.GEHA_TERMS_OF_USE]?.title,
        testId: 'terms-of-use',
      },
      {
        href: footerData[FooterLinks.GEHA_EDITORIAL_POLICY]?.url,
        label: footerData[FooterLinks.GEHA_EDITORIAL_POLICY]?.title,
        testId: 'editorial-policy',
      },
      {
        href: footerData[FooterLinks.GEHA_ACCESSIBILITY]?.url,
        label: footerData[FooterLinks.GEHA_ACCESSIBILITY]?.title,
        testId: 'accessibility',
      },
      {
        href: footerData[FooterLinks.GEHA_SURPRISE_BILLING]?.url,
        label: footerData[FooterLinks.GEHA_SURPRISE_BILLING]?.title,
        testId: 'surprise-billing',
      },
      {
        href: footerData[FooterLinks.GEHA_CONTACT_US]?.url,
        label: footerData[FooterLinks.GEHA_CONTACT_US]?.title,
        testId: 'contact-us',
      },
      {
        href: footerData[
          FooterLinks.GEHA_INFORMATION_ON_PAYMENT_OF_OUT_OF_NETWORK_BENEFITS
        ]?.url,
        label:
          footerData[
            FooterLinks.GEHA_INFORMATION_ON_PAYMENT_OF_OUT_OF_NETWORK_BENEFITS
          ]?.title,
        testId: 'payment-out-of-network-benefits',
      },
      {
        href: footerData[FooterLinks.GEHA_HEALTHCARE_FRAUD]?.url,
        label: footerData[FooterLinks.GEHA_HEALTHCARE_FRAUD]?.title,
        testId: 'healthcare-fraud',
      },
    ];

    return (
      <GehaFooterWrapper>
        {links.map((link, index) => (
          <GehaFooterLink
            data-testid={`geha-footer-link-${link.testId}`}
            href={link.href}
            key={index}
            target="_blank"
            onClick={() =>
              trackAdobeEventOnClick({
                destinationUrl: link.href,
                location: 'geha',
                name: link.label,
              })
            }
          >
            {link.label}
          </GehaFooterLink>
        ))}
      </GehaFooterWrapper>
    );
  };

  const LeasedFooter = () => {
    return null;
  };

  const footerByPortalName = {
    [PortalName.UMR]: UmrFooter,
    [PortalName.GEHA]: GehaFooter,
    [PortalName.STUDENT_RESOURCE]: UmrFooter,
    [PortalName.ALL_SAVERS]: UmrFooter,
    [PortalName.UHONE]: UmrFooter,
    [PortalName.UMR_LEASED]: LeasedFooter,
  };

  const FooterLayout = footerByPortalName[portalName] || UmrFooter;

  return <FooterLayout />;
};
